<template>
  <div class="referral-links">
    <div class="vx-row">
      <div class="vx-col w-full lg:w-2/3">
        <vx-card title="Create a referral link">
          <p class="mb-4 text-sm" style="color:#888">
            For an influencer, partner or loyal regular who'll spread the word for you. Enter
            their name and email and we'll create their own personal referral link — every
            customer who arrives through it is counted as referred by them.
            <strong>We don't email them anything</strong> — copy the link and send it to them
            yourself (WhatsApp, DM, email, however you normally chat).
          </p>

          <div v-if="programOff" class="mb-4 p-3" style="background:#fff4e5;border-radius:6px;color:#b26a00;font-size:13px">
            Your referral program is currently <strong>off</strong>, so links can't be created.
            <template v-if="!$store.getters['dash/isCollaborator']">Turn it on under <router-link to="/dash/settings/referrals" class="font-semibold">Settings &rsaquo; Referrals</router-link> first.</template>
            <template v-else>Ask the account owner to turn it on first.</template>
          </div>

          <div class="mb-4">
            <vs-input class="w-full mb-2" v-model="form.name" placeholder="Their name" />
            <vs-input class="w-full" v-model="form.email" placeholder="Their email" />
          </div>
          <vs-button :disabled="creating || programOff" @click="createLink">
            <span v-if="creating" class="btn-spin mr-2"></span>{{ creating ? 'Creating…' : 'Create their link' }}
          </vs-button>

          <div v-if="createdLink" class="mt-4 p-3" style="background:#e6f4ea;border-radius:6px">
            <p class="text-sm mb-2" style="color:#1e7e34">
              <strong>{{ createdLink.reused ? 'This person already had a link — here it is:' : 'Link created — copy it and send it to them:' }}</strong>
            </p>
            <div class="flex items-center">
              <vs-input class="w-full mr-2" :value="createdLink.url" readonly />
              <vs-button type="border" @click="copyLink(createdLink.url)">Copy</vs-button>
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <vx-card title="Links you've created" class="mt-base">
      <div v-if="loadingLinks" class="text-center py-10">
        <div class="w-8 h-8 pt_loading inline-block"></div>
        <p class="mt-2 text-light">Loading links...</p>
      </div>
      <div v-else-if="links.length === 0" class="text-sm" style="color:#888">
        No links yet. Create one above — it'll show up here so you can copy it again any time.
      </div>
      <vs-table v-else :data="links" stripe>
        <template slot="thead">
          <vs-th>Name</vs-th>
          <vs-th>Email</vs-th>
          <vs-th>Link</vs-th>
          <vs-th>Created</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr :key="i" v-for="(tr, i) in data">
            <vs-td :data="data[i].referrerName">{{ data[i].referrerName || '—' }}</vs-td>
            <vs-td :data="data[i].referrerEmail">
              <span class="text-sm" style="color:#999">{{ data[i].referrerEmail }}</span>
            </vs-td>
            <vs-td :data="data[i].url">
              <span class="text-sm link-cell">{{ data[i].url }}</span>
              <vs-button size="small" type="border" class="ml-2" @click="copyLink(data[i].url)">Copy</vs-button>
            </vs-td>
            <vs-td :data="data[i].createdAtS">
              <span class="text-sm" style="color:#999">{{ formatDate(data[i].createdAtS) }}</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>
  </div>
</template>

<script>
import referralsMixin from './referralsMixin.js'

export default {
  mixins: [referralsMixin],
  data () {
    return {
      form: { name: '', email: '' },
      creating: false,
      createdLink: null,   // { token, url, reused } after a successful create
      links: [],
      loadingLinks: false,
      programOff: false
    }
  },
  methods: {
    loadLinks () {
      this.loadingLinks = true
      this.$store.dispatch('dash/FETCH_REFERRALS').then((res) => {
        this.loadingLinks = false
        this.links = (res && res.dashboardLinks) || []
        if (res && res.referralSettings) this.programOff = !res.referralSettings.enabled
      }).catch((e) => {
        this.loadingLinks = false
        this.notifyError(this.referralErrorText(e, 'Could not load your referral links'))
      })
    },

    createLink () {
      if (!this.form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.notifyError('Please enter a valid email address')
        return
      }
      this.creating = true
      this.$store.dispatch('dash/CREATE_REFERRAL_LINK', {
        name: this.form.name,
        email: this.form.email
      }).then((res) => {
        this.creating = false
        if (res && res.success) {
          this.createdLink = { token: res.token, url: res.url, reused: !!res.reused }
          this.notifySuccess(res.reused ? 'This person already had a link — we found it for you' : 'Referral link created')
          this.loadLinks()
        } else {
          this.notifyError('Could not create the link. Please try again.')
        }
      }).catch((e) => {
        this.creating = false
        this.notifyError(this.referralErrorText(e, 'Could not create the link'))
      })
    },

    copyLink (url) {
      const done = () => this.notifySuccess('Link copied — paste it anywhere')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(() => { this.fallbackCopy(url, done) })
      } else {
        this.fallbackCopy(url, done)
      }
    },
    fallbackCopy (url, done) {
      const ta = document.createElement('textarea')
      ta.value = url
      ta.setAttribute('readonly', '')
      ta.style.position = 'absolute'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        done()
      } catch (e) {
        this.notifyError('Could not copy automatically — select the link and copy it manually')
      }
      document.body.removeChild(ta)
    },

    formatDate (s) {
      if (!s) return '—'
      return new Date(s * 1000).toLocaleDateString()
    }
  },
  created () {
    this.loadLinks()
  }
}
</script>

<style scoped>
.btn-spin {
  display: inline-block;
  width: 0.9em;
  height: 0.9em;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}
.link-cell {
  word-break: break-all;
}
</style>
